import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {BehaviorSubject, Observable} from 'rxjs';
import {environment} from '../../../environments/environment';
import {map, switchMap, take, tap} from 'rxjs/operators';
import {
    NoticeBoard,
    NoticeBoardMonth,
    NoticeBoardPaginate,
    NoticeBoardProfile,
} from './notice-board.types';
import {CondominiumService} from '../condominium/condominium.service';
import {UserNode} from '../../core/user/user.model';
import {UserService} from '../../core/user/user.service';

@Injectable({
    providedIn: 'root'
})
export class NoticeBoardService
{
    private _notice: BehaviorSubject<NoticeBoard | null> = new BehaviorSubject(null);
    private _notices: BehaviorSubject<NoticeBoard[] | null> = new BehaviorSubject([]);
    private _months: BehaviorSubject<NoticeBoardMonth[] | null> = new BehaviorSubject([]);
    private _profiles: BehaviorSubject<NoticeBoardProfile[] | null> = new BehaviorSubject([]);

    private _noticesPaginate: BehaviorSubject<NoticeBoardPaginate | null> = new BehaviorSubject(null);

    private _user: UserNode;

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _condominiumService: CondominiumService,
        private _userService: UserService
    ) {
        this._userService.user$
            .subscribe((user) => {
                this._user = user;
            });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for notice
     */
    get notice$(): Observable<NoticeBoard> {
        return this._notice.asObservable();
    }

    /**
     * Getter for notices
     */
    get notices$(): Observable<NoticeBoard[]> {
        return this._notices.asObservable();
    }


    /**
     * Getter for notices
     */
    get noticesPaginate$(): Observable<NoticeBoardPaginate> {
        return this._noticesPaginate.asObservable();
    }

    /**
     * Getter for notices months
     */
    get months$(): Observable<NoticeBoardMonth[]> {
        return this._months.asObservable();
    }

    /**
     * Getter for notices profiles
     */
    get profiles$(): Observable<NoticeBoardProfile[]> {
        return this._profiles.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all notices
     */
    getAll(month: string = null, pagina: number = 0): Observable<NoticeBoardPaginate>
    {
        // Profile list verification
        const occupation = this._user.occupation.code;
        const profileList = this.getProfile(occupation);

        return this._httpClient.post<NoticeBoardPaginate>(`${environment.node}/posts/find`, {
            condominiumCode: this._user.condominium_active.condominium.code,
            monthRef: month,
            page: pagina,
            limit: 20,
            profileList: profileList
        }, {reportProgress: true}).pipe(
            tap((paginate: NoticeBoardPaginate) => {
                // Update notices
                this._notices.next(paginate.posts);

                // Return notices
                this._noticesPaginate.next(paginate);
            })
        );
    }

    /**
     * Create notices
     *
     * @param notice
     */
    create(notice: any): Observable<any>
    {
        return this.notices$.pipe(
            take(1),
            switchMap((notices) => this._httpClient.post<any>(`${environment.node}/posts`, notice, {reportProgress: true}).pipe(
                map((newNotice) => {

                    // Update product with the new notice
                    this._notices.next([...notices, newNotice]);

                    // Return the new notice
                    return newNotice;
                })
            ))
        );
    }

    /**
     * Update notice
     *
     * @param id
     * @param notice
     */
    update(id: string, notice: any): Observable<NoticeBoard>
    {
        return this.notices$.pipe(
            take(1),
            switchMap(notices => this._httpClient.patch<NoticeBoard>(`${environment.node}/posts/${id}`, notice, {
                    reportProgress: true
            }).pipe(
                map((updatedNotice) => {
                    // Find the index of the updated notice
                    const index = notices.findIndex(item => item.id === id);

                    // Update the notice
                    notices[index] = updatedNotice;

                    // Update the notice
                    this._notices.next(notices);

                    // Return the updated notice
                    return updatedNotice;
                })
            ))
        );
    }

    /**
     * Delete notices
     *
     * @param id
     */
    delete(id: string): Observable<any>
    {
        return this.notices$.pipe(
            take(1),
            switchMap((notices) => this._httpClient.delete<any>(`${environment.node}/posts/${id}`, {
                reportProgress: true
            }).pipe(
                map((isDeleted: boolean) => {
                    // Find the index of the deleted notice
                    const index = notices.findIndex(item => item.id === id);

                    // Delete the notice
                    notices.splice(index, 1);

                    // Update the notices
                    this._notices.next(notices);

                    // Return the removed notice
                    return isDeleted;
                })
            ))
        );
    }

    /**
     * Get all notices months
     */
    getMonths(): Observable<NoticeBoardMonth[]>
    {
        return this._httpClient.get<NoticeBoardMonth[]>(`${environment.node}/posts/months-list?condominiumCode=${this._user.condominium_active.condominium.code}`, {reportProgress: true}).pipe(
            tap((paginate: NoticeBoardMonth[]) => {
                // Update notices months
                this._months.next(paginate);

                // Return notices months
                return paginate;
            })
        );
    }

    /**
     * Get all notices profiles
     */
    getProfiles(): Observable<NoticeBoardProfile[]>
    {
        return this._httpClient.get<NoticeBoardProfile[]>(`${environment.node}/posts/profiles-list`, {reportProgress: true}).pipe(
            tap((paginate: NoticeBoardProfile[]) => {
                // Update notices months
                this._profiles.next(paginate);

                // Return notices months
                return paginate;
            })
        );
    }

    downloadAttachment(key: string): Observable<ArrayBuffer> {
        return this._httpClient.get(`${environment.node}/uploads/signed`, {
            params: {key}, reportProgress: true, responseType: 'arraybuffer'
        });
    }

    getProfile(type: string)
    {
        // 2 Síndico
        // 3 Condômino
        // 6 Conselheiro
        // 7 Inquilino
        const map = {
            'admin': [2, 3, 6, 7],
            'sindico': [2],
            'condomino': [3],
            'funcionario': [3],
            'conselheiro': [6],
            'inquilino': [7],
            'subsindico': [2],
            'concierge': [3],
            'advogado': [3],
            'consultor': [2, 3, 6, 7],
            'assessor': [2, 3, 6, 7],
            'outros': [3],
            'preposto': [2],
            'administradora': [3],
            'administrador': [3],
            'morador': [3],
            'gestor-operacional': [2],
            'cipa-sindica': [2],
        };

        return map[type] ? map[type] : undefined;
    }
}
